import { alpha, Checkbox, Paper, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from '@mui/material';
import { CarrierUnitDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { forwardRef, useMemo } from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { useListTableStyles } from '../../../../../../ui/components/pure/list-table/list-table.styles';
import { Colors } from '../../../../../../ui/theme/colors';
import { COURIER_BATCH_FIELD_LABELS, CourierBatchRow } from '../../../../logic/courier-batch.map';
import { isRowSelectable, summariseSelection } from '../../../../logic/courier-batch.utils';
import { BatchImportCouriersActionHandlers } from '../batch-import-couriers-action.hook';
import {
  COURIER_BATCH_TEXT_FIELDS,
  CourierBatchCellClasses,
  CourierBatchGridRow,
  CourierBatchRowHandlers
} from './courier-batch-grid-row.component';

const COLUMNS: { id: string; header: string; width: string }[] = [
  { id: 'select', header: '', width: '96px' },
  ...COURIER_BATCH_TEXT_FIELDS.map((field) => ({
    id: field,
    header: COURIER_BATCH_FIELD_LABELS[field].toUpperCase(),
    width: field === 'mobile' ? '160px' : field === 'accessCode' ? '120px' : '140px'
  })),
  { id: 'groupName', header: COURIER_BATCH_FIELD_LABELS.groupName.toUpperCase(), width: '220px' },
  { id: 'status', header: 'STATUS', width: '140px' }
];

/** What a row needs from the grid, passed through the virtualised table so its components stay defined once. */
interface CourierBatchGridContext {
  isDisabled: boolean;
  onToggleRow(rowID: string): void;
}

// Controls handle their own clicks. A click anywhere else on a row ticks or unticks it.
const INTERACTIVE_ELEMENTS = 'input, textarea, button, a, [role="button"], [role="option"], [role="listbox"]';

// Defined once: new components on each render would remount every row.
const tableComponents: TableComponents<CourierBatchRow, CourierBatchGridContext> = {
  Table: (props) => <Table {...props} style={{ tableLayout: 'fixed', borderCollapse: 'separate' }} />,
  // MUI v9 TableHead is an OverridableComponent, which Virtuoso's ComponentType slot rejects.
  TableHead: (props) => <TableHead {...(props as any)} />,
  TableRow: ({ item, context, ...props }) => {
    const canToggle = !!context && !context.isDisabled && isRowSelectable(item);
    return (
      <TableRow
        {...props}
        selected={item.isSelected}
        // A row that failed validation takes the selected tint in the error colour. `&&` outranks the shared list
        // table's hover rule, which would otherwise replace the tint.
        sx={
          item.status === 'error'
            ? ({ palette }) => ({
                backgroundColor: alpha(palette.error.main, palette.action.selectedOpacity),
                '&&:hover': {
                  backgroundColor: alpha(
                    palette.error.main,
                    palette.action.selectedOpacity + palette.action.hoverOpacity
                  )
                }
              })
            : undefined
        }
        style={{ ...props.style, cursor: canToggle ? 'pointer' : undefined }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          // The picker's options and the tooltips render outside the row, but their clicks still
          // bubble here through React, so only a click inside the row itself counts.
          if (!canToggle || !event.currentTarget.contains(target) || target.closest(INTERACTIVE_ELEMENTS)) {
            return;
          }
          context?.onToggleRow(item.rowID);
        }}
      />
    );
  },
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
};

export function CourierBatchGrid({
  rows,
  units,
  handlers,
  isDisabled,
  validatingRowIDs
}: {
  rows: CourierBatchRow[];
  units: CarrierUnitDto[];
  handlers: CourierBatchRowHandlers & Pick<BatchImportCouriersActionHandlers, 'onToggleAll'>;
  /** A validate or upload is running: nothing in the table can be changed until it finishes. */
  isDisabled: boolean;
  /** Rows in a validate request whose response hasn't come back yet, which show a spinner. */
  validatingRowIDs: string[];
}) {
  // One object for every row, so the row memo holds.
  const { onCellChange, onCarrierChange, onToggleRow } = handlers;
  const rowHandlers = useMemo(
    () => ({ onCellChange, onCarrierChange, onToggleRow }),
    [onCellChange, onCarrierChange, onToggleRow]
  );

  // Over the rows that can be ticked: rows with errors, in flight, or imported don't count.
  const { selectable, selected } = useMemo(() => summariseSelection(rows), [rows]);
  const validating = useMemo(() => new Set(validatingRowIDs), [validatingRowIDs]);
  const context = useMemo(() => ({ isDisabled, onToggleRow }), [isDisabled, onToggleRow]);

  // Worked out once here, not per cell: the hook's dynamic rules would otherwise build a style sheet
  // for every one of the grid's cells. The cell classes are static, so every row can share them.
  const isTouchDevice = useMediaQuery('(hover: none)');
  const classes = useListTableStyles({ hasRows: rows.length > 0, isTouchDevice });
  const cellClasses: CourierBatchCellClasses = useMemo(
    () => ({ tableCell: classes.tableCell, tableFont: classes.tableFont, tableCellContent: classes.tableCellContent }),
    [classes.tableCell, classes.tableFont, classes.tableCellContent]
  );
  const isAllSelected = selectable > 0 && selected === selectable;

  return (
    <Paper className={classes.tableWrapper} variant="outlined" style={{ marginBottom: 0, overflow: 'hidden' }}>
      <TableVirtuoso
        className={classes.table}
        style={{ height: '60vh' }}
        data={rows}
        components={tableComponents}
        context={context}
        computeItemKey={(_index, row) => row.rowID}
        fixedHeaderContent={() => (
          <TableRow style={{ backgroundColor: Colors.white }}>
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                className={`${classes.tableHeaderFont} ${classes.header}`}
                style={{ width: column.width, minWidth: column.width, whiteSpace: 'nowrap' }}
              >
                {column.id === 'select' ? (
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={isAllSelected}
                    indeterminate={selected > 0 && !isAllSelected}
                    disabled={isDisabled || selectable === 0}
                    onChange={() => handlers.onToggleAll(!isAllSelected)}
                    slotProps={{
                      input: { 'aria-label': 'Select all couriers for upload' }
                    }}
                  />
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
        )}
        itemContent={(index, row) => (
          <CourierBatchGridRow
            row={row}
            rowNumber={index + 1}
            units={units}
            handlers={rowHandlers}
            cellClasses={cellClasses}
            isDisabled={isDisabled}
            isValidating={validating.has(row.rowID)}
          />
        )}
      />
    </Paper>
  );
}
